import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, AlertCircle, CheckCircle, RotateCcw } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAppSettings, AppSetting } from "@/hooks/useAppSettings";
import { toast } from "@/hooks/use-toast";
import { AudioSettings } from "@/components/AudioSettings";
import { validateSettingValue } from "@/lib/settings-validation";

function getInitialValue(val: any) {
  // value is jsonb; keep as-is
  return val;
}

function isDescriptionField(s: AppSetting) {
  return s.key.toLowerCase().includes("description");
}

export function AdminSettings() {
  const { settings, loading, saving, error, saveSettings, resetAllToDefaults } = useAppSettings();
  const [edited, setEdited] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, AppSetting[]>();
    for (const s of settings) {
      if (!map.has(s.category)) map.set(s.category, []);
      map.get(s.category)!.push(s);
    }
    return Array.from(map.entries()).map(([category, items]) => ({ category, items }));
  }, [settings]);

  const handleChange = (id: string, value: any) => {
    const setting = settings.find(s => s.id === id);
    if (!setting) return;
    
    // Validate the new value
    const validation = validateSettingValue(setting, value);
    
    // Update validation errors
    setValidationErrors(prev => {
      if (validation.isValid) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: validation.error! };
    });
    
    // Store value even if invalid (allows user to correct it)
    setEdited((prev) => ({ ...prev, [id]: value }));
  };

  const getValue = (s: AppSetting) =>
    Object.prototype.hasOwnProperty.call(edited, s.id) ? edited[s.id] : getInitialValue(s.value);

  const handleResetAll = async () => {
    setIsResetting(true);
    
    const result = await resetAllToDefaults();
    
    if (result.success) {
      toast({
        title: "Settings Reset",
        description: "All settings have been restored to their default values.",
      });
      setEdited({});
      setValidationErrors({});
    } else {
      toast({
        title: "Reset Failed",
        description: result.error || "Failed to reset settings. Please try again.",
        variant: "destructive",
      });
    }
    
    setIsResetting(false);
    setShowResetDialog(false);
  };

  const onSave = async () => {
    // Validate all edited settings
    const errors: Record<string, string> = {};
    
    for (const id of Object.keys(edited)) {
      const setting = settings.find(s => s.id === id);
      if (!setting) continue;
      
      const validation = validateSettingValue(setting, edited[id]);
      if (!validation.isValid) {
        errors[id] = validation.error!;
      }
    }
    
    // If there are validation errors, show toast and don't save
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast({ 
        title: "Validation Failed", 
        description: `Please fix ${Object.keys(errors).length} error(s) before saving.`,
        variant: "destructive" 
      });
      return;
    }
    
    const rows = Object.keys(edited).map((id) => {
      const originalSetting = settings.find(s => s.id === id);
      if (!originalSetting) return null;
      return {
        id,
        value: edited[id],
        category: originalSetting.category,
        key: originalSetting.key,
        display_name: originalSetting.display_name,
        description: originalSetting.description,
        setting_type: originalSetting.setting_type,
        options: originalSetting.options,
        is_public: originalSetting.is_public,
      };
    }).filter(Boolean);
    
    const res = await saveSettings(rows);
    if (res.success) {
      toast({ title: "Settings saved", description: "Your changes were applied successfully." });
      setEdited({});
      setValidationErrors({});
    } else {
      toast({ title: "Save failed", description: res.error || "Unknown error", variant: "destructive" });
    }
  };

  return (
    <main>
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Platform Settings</h1>
        <div className="flex items-center gap-2">
          <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
            <AlertDialogTrigger asChild>
              <Button 
                variant="outline" 
                disabled={loading || saving || isResetting}
                className="text-destructive hover:text-destructive"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset All to Defaults
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset All Settings?</AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <p>
                    This will restore <strong>all {settings.length} settings</strong> to their default values.
                  </p>
                  <p className="text-destructive font-medium">
                    This action cannot be undone. Any custom configurations will be lost.
                  </p>
                  {Object.keys(edited).length > 0 && (
                    <p className="text-amber-600 dark:text-amber-400">
                      Warning: You have {Object.keys(edited).length} unsaved change(s) that will be discarded.
                    </p>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    handleResetAll();
                  }}
                  disabled={isResetting}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  {isResetting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset All Settings
                    </>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button 
            variant="outline" 
            disabled={Object.keys(edited).length === 0 || saving || isResetting} 
            onClick={() => {
              setEdited({});
              setValidationErrors({});
            }}
          >
            Discard Changes
          </Button>
          <Button 
            onClick={onSave} 
            disabled={
              Object.keys(edited).length === 0 || 
              Object.keys(validationErrors).length > 0 || 
              saving ||
              isResetting
            }
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </header>

      {Object.keys(validationErrors).length > 0 && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Validation Errors</AlertTitle>
          <AlertDescription>
            Please fix {Object.keys(validationErrors).length} error(s) before saving:
            <ul className="list-disc list-inside mt-2">
              {Object.keys(validationErrors).slice(0, 3).map(id => {
                const setting = settings.find(s => s.id === id);
                return (
                  <li key={id}>
                    <strong>{setting?.display_name}:</strong> {validationErrors[id]}
                  </li>
                );
              })}
              {Object.keys(validationErrors).length > 3 && (
                <li>...and {Object.keys(validationErrors).length - 3} more</li>
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-16 w-16 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Audio Settings Card */}
          <div className="lg:col-span-2">
            <AudioSettings />
          </div>
          
          {/* Existing Settings */}
          {grouped.map(({ category, items }) => (
            <section key={category}>
              <Card>
                <CardHeader>
                  <CardTitle className="capitalize">{category.replace(/_/g, " ")}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {items.map((s) => (
                    <div key={s.id} className="space-y-2">
                      <Label htmlFor={s.id} className="flex items-center gap-2">
                        {s.display_name}
                        {edited[s.id] !== undefined && !validationErrors[s.id] && (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        )}
                        {validationErrors[s.id] && (
                          <AlertCircle className="h-4 w-4 text-destructive" />
                        )}
                      </Label>
                      <p className="text-sm text-muted-foreground">{s.description}</p>

                      {s.setting_type === "boolean" ? (
                        <div className="flex items-center gap-3 pt-1">
                          <Switch
                            id={s.id}
                            checked={Boolean(getValue(s))}
                            onCheckedChange={(v) => handleChange(s.id, v)}
                          />
                          <span className="text-sm text-muted-foreground">{Boolean(getValue(s)) ? "Enabled" : "Disabled"}</span>
                        </div>
                      ) : s.setting_type === "number" ? (
                        <div>
                          <Input
                            id={s.id}
                            type="number"
                            value={String(getValue(s) ?? "")}
                            onChange={(e) => handleChange(s.id, e.target.value === "" ? null : Number(e.target.value))}
                            className={validationErrors[s.id] ? "border-destructive" : ""}
                          />
                          {validationErrors[s.id] && (
                            <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              {validationErrors[s.id]}
                            </p>
                          )}
                        </div>
                      ) : s.setting_type === "select" ? (
                        <div>
                          <Select
                            value={String(getValue(s) ?? "")}
                            onValueChange={(v) => handleChange(s.id, v)}
                          >
                            <SelectTrigger id={s.id} className={validationErrors[s.id] ? "border-destructive" : ""}>
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                              {(Array.isArray(s.options) ? s.options : []).map((opt: any) => (
                                <SelectItem key={String(opt)} value={String(opt)}>
                                  {String(opt)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {validationErrors[s.id] && (
                            <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              {validationErrors[s.id]}
                            </p>
                          )}
                        </div>
                      ) : isDescriptionField(s) ? (
                        <div>
                          <Textarea
                            id={s.id}
                            value={String(getValue(s) ?? "")}
                            onChange={(e) => handleChange(s.id, e.target.value)}
                            rows={4}
                            className={validationErrors[s.id] ? "border-destructive" : ""}
                          />
                          {validationErrors[s.id] && (
                            <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              {validationErrors[s.id]}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div>
                          <Input
                            id={s.id}
                            type="text"
                            value={String(getValue(s) ?? "")}
                            onChange={(e) => handleChange(s.id, e.target.value)}
                            className={validationErrors[s.id] ? "border-destructive" : ""}
                          />
                          {validationErrors[s.id] && (
                            <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              {validationErrors[s.id]}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </section>
          ))}
        </div>
      )}

      <div className="flex justify-end mt-6">
        <Button 
          onClick={onSave} 
          disabled={
            Object.keys(edited).length === 0 || 
            Object.keys(validationErrors).length > 0 || 
            saving
          }
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </div>
    </main>
  );
}

export default AdminSettings;
